/**
 * PATH: apps/quote/src/components/builder/phantom-row/PhantomRow.tsx
 * PURPOSE: Excel-like phantom row with AI suggestions - grid cells layout
 */

import { Plus, Check, Sparkles, Loader2 } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { UNIT_OPTIONS } from '../units'
import { computeLine } from '@/lib/quote-builder-math'
import { usePhantomRowLogic } from './usePhantomRowLogic'
import { SuggestionsDropdown } from './SuggestionsDropdown'

interface PhantomRowProps {
  sectionId: string
  rowNumber: number
  gridStyle: React.CSSProperties
  onAddItem: (sectionId: string, data: {
    location?: string; description: string; quantity: number; unit: string;
    productivity_pmpd: number; labour_chargeout?: number | null; material_chargeout?: number | null;
    plant_chargeout?: number | null; subcon_chargeout?: number | null;
  }) => void
  isEditable: boolean
  autoFocus?: boolean
  /**
   * Quote-level config defaults from the Setup tab. New rows seed their
   * pmpd / unit fields from these so estimators see their per-quote
   * settings honoured instead of a hardcoded fallback.
   */
  configDefaults?: { defaultProductivityPmpd?: number | string | null; defaultUnit?: string }
  /**
   * Total grid track count (baseline + advanced + custom columns). Phantom
   * row only renders 17 cells; everything beyond gets a hidden placeholder
   * div so CSS-grid alignment with header + line-item rows is preserved
   * when optional/custom columns are visible.
   */
  totalGridColumns?: number
}

// Hand-coded cells in the phantom row (cols 0..16 — the 9 baseline bands).
// Anything beyond this is rendered as a width-zero placeholder so the
// phantom doesn't paint past the editable inputs.
const PHANTOM_RENDERED_COLUMNS = 17

export default function PhantomRow({ sectionId, rowNumber, gridStyle, onAddItem, isEditable, autoFocus = false, configDefaults, totalGridColumns }: PhantomRowProps) {
  const {
    values, setValues, isActive, setIsActive, showSuggestions, setShowSuggestions,
    suggestions, selectedSuggestionIndex, setSelectedSuggestionIndex,
    refs, commit, handleKeyDown, applySuggestion, isFetchingSuggestions,
  } = usePhantomRowLogic({ sectionId, autoFocus, onAddItem, configDefaults })

  if (!isEditable) return null

  const bg = isActive
    ? 'bg-blue-50/50 dark:bg-blue-900/10 border-t-2 border-blue-300 dark:border-blue-700'
    : 'bg-slate-50/30 dark:bg-slate-900/20 hover:bg-blue-50/30 dark:hover:bg-blue-900/5'

  // Live frontend compute on the phantom row so Hrs/Days/Calc-rate cells fill
  // in immediately as the user types. Mirrors LineItemRow behaviour.
  const live = computeLine({
    quantity: values.quantity,
    productivity_pmpd: values.productivity_pmpd,
    labour_chargeout: values.labour_chargeout,
    material_chargeout: values.material_chargeout,
    plant_chargeout: values.plant_chargeout,
    subcon_chargeout: values.subcon_chargeout,
  })

  const cellInput = (ref: React.RefObject<HTMLInputElement | null>, field: string, val: string, opts?: { type?: string; step?: string; placeholder?: string; className?: string }) => (
    <input
      ref={ref} type={opts?.type || 'text'} step={opts?.step} value={val}
      onChange={(e) => { setValues(p => ({ ...p, [field]: e.target.value })); setIsActive(true) }}
      onKeyDown={(e) => handleKeyDown(e, field)} placeholder={opts?.placeholder || ''}
      className={cn('w-full h-full px-1 text-[11px] bg-transparent border-0 focus:ring-1 focus:ring-blue-400 rounded outline-none', opts?.className)}
      tabIndex={isActive ? 0 : -1}
    />
  )

  return (
    <div className={cn('grid transition-colors relative', bg)} style={gridStyle} onClick={() => !isActive && refs.descRef.current?.focus()}>
      {/* # */}
      <div className="p-1 border-r border-slate-200 dark:border-slate-700 flex items-center justify-center text-[10px] text-slate-400">
        {isActive ? (
          <button
            onClick={(e) => { e.stopPropagation(); commit() }}
            className={cn(
              'h-5 w-5 flex items-center justify-center rounded transition-colors',
              values.description.trim()
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-slate-200 dark:bg-slate-700 hover:bg-blue-600 hover:text-white text-blue-600 dark:text-blue-400'
            )}
            title="Add row (Enter)"
          >
            {values.description.trim() ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          </button>
        ) : rowNumber}
      </div>
      {/* Location */}
      <div className="border-r border-slate-200 dark:border-slate-700">{cellInput(refs.locRef, 'location', values.location)}</div>
      {/* Description with AI suggestions */}
      <div className="border-r border-slate-200 dark:border-slate-700 relative">
        <div className="flex items-center h-full">
          <input ref={refs.descRef} data-phantom-desc={sectionId} value={values.description}
            onChange={(e) => { setValues(p => ({ ...p, description: e.target.value })); setIsActive(true) }}
            onKeyDown={(e) => handleKeyDown(e, 'description')} onFocus={() => setIsActive(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={isActive ? 'Type description...' : 'Click to add item...'}
            className={cn('w-full h-full px-1.5 text-[11px] bg-transparent border-0 focus:ring-1 focus:ring-blue-400 rounded outline-none', !isActive && 'cursor-pointer text-slate-400 italic')}
          />
          {isFetchingSuggestions && <Loader2 className="w-3 h-3 text-blue-500 animate-spin mr-1" />}
          {suggestions.length > 0 && isActive && <Sparkles className="w-3 h-3 text-amber-500 mr-1" />}
        </div>
        {showSuggestions && suggestions.length > 0 && (
          <SuggestionsDropdown suggestions={suggestions} selectedIndex={selectedSuggestionIndex} onSelect={applySuggestion} onHover={setSelectedSuggestionIndex} />
        )}
      </div>
      {/* Application placeholder */}
      <div className="border-r border-slate-200 dark:border-slate-700 bg-indigo-50/20 dark:bg-indigo-950/10 text-slate-400 text-[10px] px-1 flex items-center"><span className="truncate">—</span></div>
      {/* Quantity */}
      <div className="border-r border-slate-200 dark:border-slate-700">{cellInput(refs.qtyRef, 'quantity', values.quantity, { type: 'number', placeholder: '1', className: 'text-right font-mono' })}</div>
      {/* Unit */}
      <div className="border-r border-slate-200 dark:border-slate-700">
        <select ref={refs.unitRef} value={values.unit} onChange={(e) => { setValues(p => ({ ...p, unit: e.target.value })); setIsActive(true) }}
          onKeyDown={(e) => handleKeyDown(e, 'unit')} className="w-full h-full text-[10px] bg-transparent border-0 focus:ring-1 focus:ring-blue-400 rounded outline-none text-center" tabIndex={isActive ? 0 : -1}>
          {UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
        </select>
      </div>
      {/* PMPD */}
      <div className="border-r border-slate-200 dark:border-slate-700 bg-green-50/30 dark:bg-green-950/20">{cellInput(refs.prodRef, 'productivity_pmpd', values.productivity_pmpd, { type: 'number', placeholder: '15', className: 'text-right font-mono text-green-700 dark:text-green-400' })}</div>
      {/* Labour Hours — live: qty / pmpd × 8 */}
      <div
        className="border-r border-slate-200 dark:border-slate-700 text-[10px] text-right px-1 flex items-center justify-end tabular-nums bg-blue-50/30 dark:bg-blue-950/20 text-slate-500 dark:text-slate-400"
        title={`Hours = qty / pmpd × 8 hr/day. Live: ${live.labour_hours.toFixed(2)} hr`}
      >
        {live.labour_hours > 0 ? live.labour_hours.toFixed(1) : '—'}
      </div>
      {/* Labour Days — live: qty / pmpd */}
      <div
        className="border-r border-slate-200 dark:border-slate-700 text-[10px] text-right px-1 flex items-center justify-end tabular-nums bg-blue-50/30 dark:bg-blue-950/20 text-slate-500 dark:text-slate-400"
        title={`Days = qty / pmpd. Live: ${live.labour_days.toFixed(3)} d`}
      >
        {live.labour_days > 0 ? live.labour_days.toFixed(2) : '—'}
      </div>
      {/* Labour C/O */}
      <div className="border-r border-slate-200 dark:border-slate-700 bg-blue-50/30 dark:bg-blue-950/20">{cellInput(refs.labourRef, 'labour_chargeout', values.labour_chargeout, { type: 'number', step: '0.01', className: 'text-right tabular-nums' })}</div>
      {/* Calc rate — live: labour_chargeout / qty */}
      <div
        className="border-r border-slate-200 dark:border-slate-700 text-[10px] text-right px-1 flex items-center justify-end tabular-nums bg-yellow-50/30 dark:bg-yellow-950/20 text-slate-500 dark:text-slate-400"
        title={`Calculated rate = labour_chargeout / qty. Live: ${live.rate_per_unit.toFixed(2)} $/unit`}
      >
        {live.rate_per_unit > 0 ? formatCurrency(live.rate_per_unit) : '—'}
      </div>
      {/* Adj rate placeholder (entered after row creation in inspector) */}
      <div className="border-r border-slate-200 dark:border-slate-700 text-slate-400 text-[10px] text-right px-1 flex items-center justify-end">—</div>
      {/* Material C/O */}
      <div className="border-r border-slate-200 dark:border-slate-700 bg-amber-50/30 dark:bg-amber-950/20">{cellInput(refs.materialRef, 'material_chargeout', values.material_chargeout, { type: 'number', step: '0.01', className: 'text-right tabular-nums' })}</div>
      {/* Plant C/O */}
      <div className="border-r border-slate-200 dark:border-slate-700 bg-purple-50/30 dark:bg-purple-950/20">{cellInput(refs.plantRef, 'plant_chargeout', values.plant_chargeout, { type: 'number', step: '0.01', className: 'text-right tabular-nums' })}</div>
      {/* Subcon C/O */}
      <div className="border-r border-slate-200 dark:border-slate-700 bg-teal-50/30 dark:bg-teal-950/20">{cellInput(refs.subconRef, 'subcon_chargeout', values.subcon_chargeout, { type: 'number', step: '0.01', className: 'text-right tabular-nums' })}</div>
      {/* Margin % — only meaningful once cost fields are entered (handled in row inspector). Show — for now. */}
      <div className="border-r border-slate-200 dark:border-slate-700 text-slate-400 text-[10px] text-center flex items-center justify-center">—</div>
      {/* Total — live sum of all chargeouts */}
      <div
        className="text-[10px] text-right px-1 flex items-center justify-end tabular-nums bg-emerald-50/30 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 font-semibold"
        title={`Subtotal = labour + material + plant + subcon chargeouts. Live: $${live.chargeout_total.toFixed(2)}`}
      >
        {live.chargeout_total > 0 ? formatCurrency(live.chargeout_total) : '-'}
      </div>

      {/* Trailing placeholders — keep grid alignment when advanced + custom
          columns are visible. CSS-grid maps children 1:1 to template tracks,
          so we MUST render one filler div per extra track or the visible
          phantom inputs will drift left of the header / line-item cells. */}
      {Array.from({
        length: Math.max(0, (totalGridColumns ?? PHANTOM_RENDERED_COLUMNS) - PHANTOM_RENDERED_COLUMNS),
      }).map((_, i) => (
        <div
          key={`phantom-filler-${i}`}
          aria-hidden="true"
          className="border-r border-slate-200 dark:border-slate-700 last:border-r-0"
        />
      ))}
    </div>
  )
}
